import { useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { ROLE_INFO, TEAM_NAMES, type GameKind, type ProfileDto } from '@mafia/core';
import { formatDate } from '@/lib/format';
import { KindBadge, KindTabs } from './Kind';
import { Card, Empty } from './ui';

export function StatTile({ label, value, tone }: { label: string; value: string | number; tone?: 'green' | 'red' | 'gold' }) {
  return (
    <div className="rounded-2xl border border-white/5 bg-ink-900/80 p-4">
      <p className="text-xs uppercase tracking-wide text-zinc-500">{label}</p>
      <p
        className={clsx(
          'mt-1 text-3xl font-semibold tabular-nums',
          tone === 'green' && 'text-emerald-300',
          tone === 'red' && 'text-blood-400',
          tone === 'gold' && 'text-gold-300',
        )}
      >
        {value}
      </p>
    </div>
  );
}

/** Сводка, винрейт по ролям и последние игры — общая для профиля и админки. */
export function ProfileStats({ profile }: { profile: ProfileDto }) {
  const [kind, setKind] = useState<GameKind | 'ALL'>('ALL');
  // Любительская и турнирная статистика ведутся раздельно; «Все игры» — их сумма.
  const stats = kind === 'ALL' ? { totals: profile.totals, byRole: profile.byRole } : (profile.byKind?.[kind] ?? { totals: profile.totals, byRole: [] });
  const t = stats.totals;
  const recent = kind === 'ALL' ? profile.recentGames : profile.recentGames.filter((g) => g.kind === kind);
  return (
    <div className="space-y-4">
      <KindTabs value={kind} onChange={(k) => setKind(k)} withAll />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <StatTile label="Баллы" value={t.points} tone="gold" />
        <StatTile label="Сыграно" value={t.played} />
        <StatTile label="Победы" value={t.won} tone="green" />
        <StatTile label="Поражения" value={t.lost} tone="red" />
        <StatTile label="Винрейт" value={`${t.winRate}%`} />
      </div>

      <Card className="p-5">
        <h2 className="mb-4 font-display text-lg">Винрейт по ролям</h2>
        {stats.byRole.length === 0 ? (
          <p className="text-sm text-zinc-500">Пока нет сыгранных игр.</p>
        ) : (
          <div className="space-y-3">
            {stats.byRole.map((r) => (
              <div key={r.roleId}>
                <div className="mb-1 flex justify-between text-sm">
                  <span>
                    {ROLE_INFO[r.roleId].emoji} {ROLE_INFO[r.roleId].name}
                  </span>
                  <span className="text-zinc-400">
                    {r.won}/{r.played} · <b className="text-zinc-100">{r.winRate}%</b> · {r.points} балл.
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-ink-700">
                  <div className="h-full rounded-full bg-gradient-to-r from-gold-500 to-gold-300" style={{ width: `${r.winRate}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="overflow-hidden">
        <h2 className="px-5 pt-5 font-display text-lg">Последние игры</h2>
        {recent.length === 0 ? (
          <Empty icon="🃏" title="Игр ещё не было" />
        ) : (
          <div className="overflow-x-auto">
            <table className="table-base mt-3">
              <thead>
                <tr>
                  <th>Дата</th>
                  <th>Роль</th>
                  <th>Победитель</th>
                  <th className="text-right">Баллы</th>
                  <th className="text-right">Итог</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((g) => (
                  <tr key={g.gameId} className="hover:bg-white/[.02]">
                    <td className="whitespace-nowrap text-zinc-400">
                      <Link to={`/games/${g.gameId}`} className="hover:text-white">
                        {formatDate(g.endedAt)}
                      </Link>
                    </td>
                    <td>
                      {ROLE_INFO[g.role].emoji} {ROLE_INFO[g.role].name} <KindBadge kind={g.kind} />
                    </td>
                    <td className="text-zinc-400">{g.winner ? TEAM_NAMES[g.winner] : '—'}</td>
                    <td className="text-right font-semibold tabular-nums text-gold-300">{g.points}</td>
                    <td className={clsx('text-right font-medium', g.isWinner ? 'text-emerald-300' : 'text-blood-400')}>
                      {g.isWinner ? 'Победа' : 'Поражение'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
